import React, { useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import classNames from 'classnames'

import { useOutsideClick } from '../../common/useOutsideClick'

import { Button } from '../../components/Button/Button'
import { Spinner } from '../../components/Spinner/Spinner'

import './AIAssistantDropdown.scss'

export interface AIAssistantDropdownOption {
  id: string
  label: string
  disabled?: boolean
  tooltipTitle?: string
  divider?: boolean
  loading?: boolean
}

interface AIAssistantDropdownProps {
  options: AIAssistantDropdownOption[]
  onOptionSelected: (optionId: string) => void
}

export const AIAssistantDropdown: React.FC<AIAssistantDropdownProps> = ({ options, onOptionSelected }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  useOutsideClick(containerRef, () => {
    setIsOpen(false)
  })

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
  }

  return (
    <div ref={containerRef} className='position-relative w-100'>
      <Button className='px-3 zocks-py-0.75 zocks-gap-1 rounded-3' variant='dark' onClick={toggleDropdown}>
        <div className='ai-assistant-dropdown-icon-wrapper d-flex align-items-center justify-content-center flex-shrink-0'>
          <i className='bi bi-stars' />
        </div>
        <div className='d-flex flex-column'>
          <span className='paragraph-medium bold'>AI Assistant</span>
          <span className='paragraph-small'>Ask questions about this contact</span>
        </div>
        {isOpen ? <i className='bi bi-chevron-contract' /> : <i className='bi bi-chevron-expand' />}
      </Button>
      <div className={`dropdown-menu ${isOpen ? 'show' : ''} paragraph-small w-100 border-0 rounded-1 mt-2 p-2 ai-assistant-dropdown-menu`}>
        {options.map(({ id, label, disabled, tooltipTitle, divider, loading }) => {
          if (divider) {
            return <div key={id} className='dropdown-divider' />
          }

          if (loading) {
            return (
              <div key={id} className='py-1 px-2 ai-assistant-dropdown-option ai-assistant-dropdown-option-disabled d-flex align-items-center zocks-gap-1'>
                <Spinner size='sm' />
                {label}
              </div>
            )
          }

          const handleOptionSelected = () => {
            onOptionSelected(id)
          }

          const optionProps = {
            ...(!disabled && { role: 'button', onClick: handleOptionSelected }),
          }

          return (
            <OverlayTrigger key={id} trigger={disabled ? ['hover', 'focus'] : []} overlay={<Tooltip>{tooltipTitle}</Tooltip>}>
              <div
                className={classNames('py-1 px-2 ai-assistant-dropdown-option', {
                  'ai-assistant-dropdown-option-disabled': disabled,
                })}
                {...optionProps}
              >
                {label}
              </div>
            </OverlayTrigger>
          )
        })}
      </div>
    </div>
  )
}
